import { useForm } from '@tanstack/react-form'
import {
  invitarUsuarioSchema,
  DEFAULT_USUARIO_VALUES,
  type InvitarUsuarioFormValues,
} from './usuarioSchema'

export interface UseUsuarioFormOptions {
  defaultValues?: Partial<InvitarUsuarioFormValues>
  onSubmit?: (data: InvitarUsuarioFormValues) => Promise<void> | void
}

/**
 * Hook para manejar el formulario de invitación de usuario con TanStack Form + Zod.
 */
export function useUsuarioForm(options: UseUsuarioFormOptions = {}) {
  const { defaultValues, onSubmit } = options

  const form = useForm({
    defaultValues: {
      ...DEFAULT_USUARIO_VALUES,
      ...defaultValues,
    } as InvitarUsuarioFormValues,
    validators: {
      onSubmit: ({ value }) => {
        const result = invitarUsuarioSchema.safeParse(value)
        if (!result.success) {
          return result.error.issues.map((issue) => ({
            message: issue.message,
            path: issue.path,
          }))
        }
        return undefined
      },
    },
    onSubmit: async ({ value }) => {
      if (!onSubmit) return
      await onSubmit(value as InvitarUsuarioFormValues)
    },
  })

  return { form }
}

/**
 * Helper para extraer mensaje de error de TanStack Form.
 * Compatible con Zod v4 (StandardSchemaV1Issue).
 */
export function getFieldError(errors: unknown[]): string | undefined {
  const first = errors[0]
  if (!first) return undefined
  if (typeof first === 'string') return first
  if (typeof first === 'object' && first !== null && 'message' in first) {
    return (first as { message: string }).message
  }
  return undefined
}

// Tipos exportados para uso en componentes
export type UseUsuarioFormReturn = ReturnType<typeof useUsuarioForm>
export type UsuarioForm = UseUsuarioFormReturn['form']
